import { useIDEParams } from '@common/ide/state';
import { useSelector } from '@common/performance/useSelector';
import { Entity } from '@common/types/Entity';
import { getIDEUrl } from '@common/url';
import { ConnectorIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Empty, overlayStyle } from '@prophecy/ui-v3';
import { Button } from '@prophecy/ui-v3/Button';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { disableUserSelect } from '@prophecy/ui-v3/Graph/styled';
import { Centered, Stack, StackItem } from '@prophecy/ui-v3/Layout';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { caseInsensitiveInclude } from '@prophecy/utils/string';
import { useGraphQlQuery } from 'frontend/core/src/data/util';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { ConnectorsQuery } from '../query';
import { ObservationState, actionTypes } from '../redux/types';

const ConnectorBrowserRoot = styled(Stack)`
  flex-shrink: 0;
  gap: ${theme.spaces.x16};
  height: 100%;
  width: 100%;
  ${disableUserSelect};
`;

const ConnectorListContainer = styled(Stack)`
  overflow: auto;
  height: 100%;
`;

const ConnectorItem = styled(Stack)<{ isSelected?: boolean }>`
  border-bottom: 1px solid ${theme.colors.gray200};
  height: ${theme.sizes.x40};
  padding: 0 ${theme.spaces.x4};
  ${(props) =>
    props.isSelected &&
    `
    background-color: ${theme.colors.brand50};
    border-radius: ${theme.sizes.x4};
  `}

  &:hover {
    background-color: ${theme.colors.brand50};
    border-radius: ${theme.sizes.x4};
  }
`;

export function ConnectorBrowser({ searchQuery = '' }: { searchQuery?: string }) {
  const fabricId = useSelector<ObservationState, string>((state) => state.observationMetadata.fabricId);
  const variables = {
    fabricUID: fabricId
  };
  const { data, isInitialLoading: isLoading } = useGraphQlQuery(
    [Entity.Observation, Entity.Connector, variables],
    ConnectorsQuery,
    { enabled: Boolean(fabricId) },
    variables
  );
  const { uid } = useIDEParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLoading) {
      const selectedConnectorName = data?.ListConnectorInFabric.find((connector) => connector._id === uid);
      dispatch({
        type: actionTypes.setSelectedConnectorName,
        payload: { connectorName: selectedConnectorName?.name || '' }
      });
    }
  }, [uid, isLoading, data?.ListConnectorInFabric, dispatch]);

  return isLoading ? (
    <Centered>
      <Spinner />
    </Centered>
  ) : (
    <ConnectorBrowserRoot gap={theme.spaces.x16}>
      <StackItem grow='1'>
        <ConnectorListContainer>
          {data?.ListConnectorInFabric.filter((connector) => caseInsensitiveInclude(connector.name, searchQuery)).map(
            ({ _id, name }) => (
              <Button
                variant='plain'
                block
                elementType='link'
                key={_id}
                to={getIDEUrl({ uid: _id, entity: Entity.Observation }, { observedEntity: Entity.Connector })}
                data-test-id={`connector-browser-connector-link-${_id}-link`}>
                <ConnectorItem isSelected={uid === _id} direction='horizontal' alignY='center' gap={theme.spaces.x12}>
                  <StackItem grow='1'>
                    <Stack alignY='center' direction='horizontal' gap={theme.spaces.x12}>
                      <ConnectorIcon type='extended' size='xs' color={theme.colors.white} />
                      <Ellipsis tooltipProps={{ overlayStyle }} tooltip={true}>
                        {name}
                      </Ellipsis>
                    </Stack>
                  </StackItem>
                </ConnectorItem>
              </Button>
            )
          )}
          {data?.ListConnectorInFabric.length === 0 && (
            <Centered>
              <Empty description='No connectors available.' />
            </Centered>
          )}
        </ConnectorListContainer>
      </StackItem>
    </ConnectorBrowserRoot>
  );
}
